/**
 * TenantSourcesView — `/settings/knowledge-base`
 *
 * Knowledge base de la organización. Sources que aplican a TODOS los
 * proyectos del tenant — normativa ISO, stack tecnológico aprobado,
 * lessons learned, etc. Solo `tenant_admin` puede subir/borrar; el
 * resto del equipo solo lista.
 *
 * Recurso interno del tenant (OT-0015): la ruta pasa por `TierGate` y un
 * `403 FORBIDDEN_TIER` a media sesión sustituye el panel por el aviso de
 * acceso denegado, no por el estado vacío.
 *
 * El backend valida con `require_tenant_admin` en POST/DELETE/PATCH.
 * El frontend muestra el botón "Subir" solo a admins (UX honesta —
 * los no-admin no necesitan clickear y recibir 403).
 */

import { Link } from '@tanstack/react-router'
import { canManageTenant, isAdmin as isAdminTier } from '../../lib/tiers'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'

import { useMe } from '../../auth/useMe'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { SourcesPanel } from '../../components/sources/SourcesPanel'
import { NoAccessNotice } from '../NoAccessView'

export function TenantSourcesView() {
  const { t } = useTranslation(['sources'])
  const { me } = useMe()

  const isAdmin = isAdminTier(me?.role)

  return (
    <ScrollPage>
      <div className="max-w-7xl">
      <Link
        to="/projects"
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t('settings.back_to_projects')}
      </Link>

      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
          {t('settings.eyebrow')}
        </p>
        <h1 className="font-display text-2xl font-semibold text-ink">
          {t('settings.title')}
        </h1>
        <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
          {t('settings.subtitle')}
        </p>
        {!isAdmin ? (
          <div className="mt-3 rounded-md border border-line bg-surface-muted px-3 py-2 inline-block">
            <p className="text-xs text-ink-muted">
              {t('settings.read_only_warning')}
            </p>
          </div>
        ) : null}
      </header>

      <SourcesPanel
        mode="tenant"
        canManage={canManageTenant(me?.role, me?.tenant?.access)}
        forbiddenTierFallback={<NoAccessNotice />}
      />
      </div>
    </ScrollPage>
  )
}
